import { ApolloCache, ApolloError, Reference } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { addLocalDentistTemplate, setLabDentistDefaultClinicTemplate } from 'apollo/templates/user';
import { addDentistTemplate, addWhiteLabelUserToClientTemplate } from 'apollo/templates/user/mutation.graphql';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import { Dentist, User } from 'models/user';
import React, { Fragment, FC, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddDentistForm from './add-dentist-form.component';
import { CustomerSetting } from 'models/customer-setting';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

type AddDentistFormContainerProps = {
    handleDentistCreation: (dentist: User) => void;
    handleClose: () => void;
    clinicId: string;
    showAddDentistButton: boolean;
    email: string;
    shouldCreateWLUser: boolean;
    isIsolatedUsersEnable: boolean;
    labClientCustomerSettings: CustomerSetting[];
    initialLabDefaultClinicId?: string;
};

// The form collects two lab-private extras alongside the dentist itself: `note` and `localId`
// travel with the create mutations, while `labDefaultClinic` is stored afterward by its own
// mutation and must be kept out of the create variables.
type AddDentistFormValues = Partial<User> & {
    note?: string;
    labDefaultClinic?: string;
};

const AddDentistFormContainer: FC<AddDentistFormContainerProps> = ({
    handleDentistCreation,
    handleClose,
    clinicId,
    showAddDentistButton,
    email,
    shouldCreateWLUser = false,
    isIsolatedUsersEnable,
    labClientCustomerSettings,
    initialLabDefaultClinicId,
}: AddDentistFormContainerProps) => {
    const [wlUserError, setWlUserError] = useState<Error | null>(null);
    const { t: translate } = useTranslation(['common', 'users']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    // The create mutations report back through onCompleted, which does not see the submitted
    // values, so the picked default clinic is held here until the dentist exists.
    const pendingLabDefaultClinicRef = useRef<string | undefined>(undefined);
    const updateCache = (cache: ApolloCache<User>, dentist: Dentist) => {
        cache.modify({
            fields: {
                dentists(dentistRefs: Reference | (Reference | undefined)[], { toReference }) {
                    if (dentistRefs instanceof Array) {
                        return [...dentistRefs, toReference(dentist)];
                    }
                    return [dentistRefs, toReference(dentist)];
                },
            },
        });
    };

    // No onError here on purpose: the failure is handled where it is awaited, so the dentist
    // that was already created is still reported back to the caller.
    const [setLabDentistDefaultClinic] = useMutation(setLabDentistDefaultClinicTemplate);

    /**
     * Stores the lab's default child clinic for the dentist that was just created, then hands
     * the dentist over. The stored entries are merged into the returned dentist so callers that
     * use the object directly - the lab order flow does, without refetching - see the default.
     */
    const finishDentistCreation = async (createdDentist: User) => {
        const labDefaultClinicId = pendingLabDefaultClinicRef.current;
        pendingLabDefaultClinicRef.current = undefined;
        if (!labDefaultClinicId) {
            handleDentistCreation(createdDentist);
            return;
        }
        try {
            const { data } = await setLabDentistDefaultClinic({
                variables: {
                    dentistId: createdDentist._id,
                    mainClinic: clinicId,
                    defaultClinic: labDefaultClinicId,
                },
            });
            handleDentistCreation({
                ...createdDentist,
                labDentistDefaultClinics: data?.setLabDentistDefaultClinic?.labDentistDefaultClinics,
            });
        } catch (error) {
            // The dentist exists at this point, so the creation must not be reported as failed.
            // The app toaster is used rather than this dialog's snackbar because the dialog
            // unmounts as soon as the creation is handed over.
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('users:LAB_DEFAULT_CLINIC_SAVE_FAILED'),
            });
            handleDentistCreation(createdDentist);
        }
    };

    const [addLocalDentist, { loading, error }] = useMutation(addLocalDentistTemplate, {
        onError: (err: ApolloError) => {
            console.error(err);
        },
        onCompleted: async ({ addLocalDentist }) => {
            await finishDentistCreation(addLocalDentist);
        },
        update(cache: ApolloCache<User>, { data: { addLocalDentist } }) {
            updateCache(cache, addLocalDentist);
        },
    });

    const [addDentist, { loading: dentistLoading }] = useMutation(addDentistTemplate, {
        onError: (err: ApolloError) => {
            const [firstError] = err.graphQLErrors;
            const translatedError = (firstError.extensions?.exception as any)?.otherInfo?.errorType
                ? `users:${(firstError.extensions?.exception as any)?.otherInfo?.errorType}`
                : err.message;
            setWlUserError(new Error(translate(translatedError)));
        },
        onCompleted: async ({ addDentist }) => {
            await finishDentistCreation(addDentist);
        },
        update(cache: ApolloCache<User>, { data: { addDentist } }) {
            updateCache(cache, addDentist);
        },
    });

    const [addWhiteLabelUserToClient, { loading: wlUserLoading }] = useMutation(addWhiteLabelUserToClientTemplate, {
        onError: (err: ApolloError) => {
            const [firstError] = err.graphQLErrors;
            const translatedError = (firstError.extensions?.exception as any)?.otherInfo?.errorType
                ? `users:${(firstError.extensions?.exception as any)?.otherInfo?.errorType}`
                : err.message;
            setWlUserError(new Error(translate(translatedError)));
        },
        onCompleted: async ({ addWhiteLabelUserToClient }) => {
            await finishDentistCreation(addWhiteLabelUserToClient);
        },
        update(cache: ApolloCache<User>, { data: { addWhiteLabelUserToClient } }) {
            updateCache(cache, addWhiteLabelUserToClient);
        },
    });
    return (
        <Fragment>
            {loading || wlUserLoading || dentistLoading ? <BackdropLoader isOpen={loading} /> : undefined}
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {wlUserError?.message ? <AlertSnackbar type="error" message={wlUserError?.message} /> : undefined}
            <AddDentistForm
                handleClose={handleClose}
                shouldCreateWLUser={shouldCreateWLUser}
                showAddDentistButton={showAddDentistButton}
                email={email}
                isIsolatedUsersEnable={isIsolatedUsersEnable}
                labClientCustomerSettings={labClientCustomerSettings}
                mainClinicId={clinicId}
                initialLabDefaultClinicId={initialLabDefaultClinicId}
                addDentist={(dentist) => {
                    // labDefaultClinic is not a variable on any of the create mutations - it is
                    // stored separately once the dentist exists - so it is kept out of the
                    // variables the remaining fields are spread into.
                    const { labDefaultClinic, ...dentistData } = dentist as AddDentistFormValues;
                    pendingLabDefaultClinicRef.current = labDefaultClinic || undefined;
                    if (shouldCreateWLUser) {
                        setWlUserError(null);
                        return addWhiteLabelUserToClient({
                            variables: Object.assign({ clientId: clinicId }, dentistData),
                        });
                    }
                    if (showAddDentistButton) {
                        setWlUserError(null);
                        return addDentist({
                            variables: {
                                name: {
                                    firstName: dentistData.name?.firstName,
                                    lastName: dentistData.name?.lastName,
                                },
                                email: dentistData.email,
                                organization: clinicId,
                                defaultClinic: clinicId,
                                role: 'DENTIST_USER',
                                active: true,
                                note: dentistData.note,
                            },
                        });
                    }
                    return addLocalDentist({ variables: Object.assign({ clinic: clinicId }, dentistData) });
                }}
            />
        </Fragment>
    );
};

export default AddDentistFormContainer;
